// FullMenuPage — category dashboard (all cats visible as cards on one screen).
// Click a card → drill into that category. Back button returns to dashboard.
// Bilingual + print-friendly.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useContent, useLang, UI, TONE_BG } from './content.jsx';
import { Placeholder, CoffeeArt } from './primitives.jsx';

export function FullMenuPage({ onClose }) {
  const c = useContent();
  const { t, lang } = useLang();
  const b = c.brand;

  const cats = useMemo(() => c.menu.categories.filter(x => x.id !== 'all'), [c.menu.categories]);
  // View state: 'dashboard' (grid of cards) | 'category' (items of selected)
  const [view, setView] = useState('dashboard');
  const [catId, setCatId] = useState(cats[0]?.id);
  const [query, setQuery] = useState('');
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!cats.find(x => x.id === catId)) setCatId(cats[0]?.id);
  }, [cats, catId]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (detail) setDetail(null);
        else if (view === 'category') setView('dashboard');
        else onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, view, detail]);

  useEffect(() => {
    const scrollY = window.scrollY;
    const body = document.body;
    const prev = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
      overflow: body.style.overflow,
    };
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    body.style.overflow = 'hidden';
    return () => {
      body.style.position = prev.position;
      body.style.top = prev.top;
      body.style.left = prev.left;
      body.style.right = prev.right;
      body.style.width = prev.width;
      body.style.overflow = prev.overflow;
      window.scrollTo(0, scrollY);
    };
  }, []);

  const todayLocal = new Date().toLocaleDateString(lang === 'tr' ? 'tr-TR' : 'en-US',
    { day: '2-digit', month: 'long', year: 'numeric' });

  const countByCat = useMemo(() => {
    const out = {};
    for (const it of c.menu.items) out[it.cat] = (out[it.cat] || 0) + 1;
    return out;
  }, [c.menu.items]);

  const searching = query.trim().length > 0;
  const q = query.trim().toLocaleLowerCase('tr-TR');
  const matchesQuery = (it) =>
    String(t(it.name)).toLocaleLowerCase('tr-TR').includes(q) ||
    String(t(it.note) || '').toLocaleLowerCase('tr-TR').includes(q) ||
    String(it.tag || '').toLocaleLowerCase('tr-TR').includes(q);

  const currentCat = cats.find(x => x.id === catId);
  const items = searching
    ? c.menu.items.filter(matchesQuery)
    : c.menu.items.filter(it => it.cat === catId);

  const idx = cats.findIndex(x => x.id === catId);
  const prev = () => idx > 0 && setCatId(cats[idx - 1].id);
  const next = () => idx < cats.length - 1 && setCatId(cats[idx + 1].id);

  const onPickCategory = (id) => {
    setCatId(id);
    setView('category');
    setQuery('');
  };

  return (
    <div className="fmp-root fixed inset-0 z-[110] bg-cream overflow-y-auto overscroll-contain"
      data-lenis-prevent
      style={{ animation: 'fmpIn .45s cubic-bezier(.22,.61,.36,1)' }}>
      <style>{`
        @keyframes fmpIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } }
        @keyframes cardPop { from { opacity: 0; transform: scale(.94) translateY(8px); } to { opacity: 1; transform: none; } }
        .cat-card-anim { animation: cardPop .5s cubic-bezier(.22,.61,.36,1) both; }
        .leader { flex:1; margin:0 .55em; border-bottom: 1.5px dotted rgba(74,46,30,.35); translate: 0 -.35em; }
        .ornament::before, .ornament::after {
          content: ""; flex:1; height:1px;
          background: linear-gradient(90deg, transparent, rgba(139,90,60,.5), transparent);
        }
        .ornament { display:flex; align-items:center; gap: 1rem; }
        @media print {
          .fmp-root { position: static !important; overflow: visible !important; background: white !important; }
          .fmp-noprint { display: none !important; }
          body { background: white !important; }
          .fmp-print-compact { break-inside: avoid; page-break-inside: avoid; }
        }
      `}</style>

      {/* Header */}
      <div className="fmp-noprint sticky top-0 z-20 backdrop-blur-xl bg-cream/85 border-b border-espresso/10">
        <div className="mx-auto max-w-6xl px-6 py-4 flex items-center justify-between gap-4">
          <button onClick={view === 'category' ? () => setView('dashboard') : onClose}
            className="inline-flex items-center gap-2 text-[13px] text-espresso hover:text-cocoa transition">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
            {view === 'category' ? t({ tr: 'Kategoriler', en: 'Categories' }) : t(UI.menuPage.backToHome)}
          </button>

          <button onClick={onClose} type="button"
            title={t({ tr: 'Ana sayfaya dön', en: 'Back to home' })}
            className="hidden sm:flex items-center gap-3.5 group nosel">
            <span className="relative inline-grid place-items-center" style={{ width: 60, height: 60 }}>
              <span aria-hidden className="absolute -inset-1 rounded-[22px] blur-2xl opacity-70 group-hover:opacity-100 transition-opacity"
                style={{ background: 'radial-gradient(circle at 50% 50%, rgba(201,165,117,.7), rgba(139,90,60,.18) 55%, transparent 78%)' }}/>
              <img src={b.logo || '/assets/glamm.png'} alt={t(b.name)}
                className="relative h-[60px] w-[60px] rounded-[18px] bg-cream ring-1 ring-espresso/20 shadow-[0_10px_30px_rgba(74,46,30,0.28)] p-[2px] object-contain transition-transform duration-500 group-hover:scale-[1.04]"/>
            </span>
            <div className="leading-none text-left">
              <div className="font-display font-semibold text-[24px] tracking-[-0.02em] text-espresso">{t(b.name)}</div>
              <div className="font-mono text-[11px] tracking-[0.26em] uppercase text-tan mt-1.5">{t(b.tagline)}</div>
            </div>
          </button>

          <div className="flex items-center gap-2">
            <div className="relative hidden md:block">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                className="absolute left-3 top-1/2 -translate-y-1/2 text-espresso/40">
                <circle cx="11" cy="11" r="7"/><path d="M21 21l-4.35-4.35"/>
              </svg>
              <input value={query}
                onChange={e => { setQuery(e.target.value); if (e.target.value.trim()) setView('category'); }}
                placeholder={t(UI.menu.search)}
                className="w-52 bg-white/80 border border-espresso/15 rounded-full pl-8 pr-3 py-1.5 text-[12.5px] outline-none focus:border-espresso/40"/>
              {query && (
                <button onClick={() => setQuery('')}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 h-5 w-5 rounded-full text-espresso/50 hover:bg-latte/40 grid place-items-center text-[11px]">×</button>
              )}
            </div>
            <button onClick={onClose}
              className="h-9 w-9 rounded-full bg-espresso text-cream grid place-items-center hover:bg-ink transition">✕</button>
          </div>
        </div>
      </div>

      <article className="mx-auto max-w-6xl px-6 py-10 md:py-14">
        {/* ===== DASHBOARD VIEW — all categories visible ===== */}
        {view === 'dashboard' && !searching && (
          <>
            <header className="text-center mb-10 md:mb-12">
              <div className="inline-flex items-center gap-3 font-mono text-[11px] tracking-[0.3em] uppercase text-tan mb-4">
                <span className="h-px w-10 bg-tan/50"/>
                {t(b.tagline)}
                <span className="h-px w-10 bg-tan/50"/>
              </div>
              <h1 className="font-display text-[46px] md:text-[68px] leading-[0.98] text-espresso">
                <em className="italic text-cocoa">{t(UI.menuPage.cover)}</em>
              </h1>
              <div className="mt-3 font-mono text-[11px] tracking-[0.3em] uppercase text-espresso/50">
                {todayLocal} · {t(b.city)}
              </div>
              <div className="mt-4 text-[13.5px] md:text-[14px] text-espresso/70 max-w-xl mx-auto leading-relaxed">
                {t({ tr: 'Bir kategoriye tıkla — tarifler, fiyatlar ve detaylar.',
                     en: 'Tap a category — recipes, prices and details.' })}
              </div>
            </header>

            {/* Category grid — all visible at once */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4">
              {cats.map((cat, i) => (
                <CategoryCard key={cat.id} cat={cat} i={i}
                  count={countByCat[cat.id] || 0}
                  onPick={() => onPickCategory(cat.id)}
                  t={t}/>
              ))}
            </div>

            {/* Info footer */}
            <footer className="mt-14 pt-10 border-t border-espresso/15 grid md:grid-cols-3 gap-8">
              <div>
                <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-tan mb-2">{t(UI.menuPage.notes)}</div>
                <p className="text-[12.5px] text-espresso/70 leading-relaxed">{t(UI.menuPage.notesBody)}</p>
              </div>
              <div>
                <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-tan mb-2">{t(UI.menuPage.contact)}</div>
                <div className="text-[12.5px] text-espresso/80 leading-relaxed">
                  <div>{b.email}</div>
                  <div>{b.phone}</div>
                  <div>{b.instagram}</div>
                </div>
              </div>
              <div>
                <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-tan mb-2">{t(UI.menuPage.branches)}</div>
                <div className="text-[12.5px] text-espresso/80 leading-relaxed space-y-1">
                  {c.branches.map(br => (
                    <div key={br.id}>
                      <b className="font-display text-[13px] text-espresso">{String(t(br.name)).replace(/^Glamm\s+/i, '')}</b>
                      <span className="text-espresso/55"> · {br.hours}</span>
                    </div>
                  ))}
                </div>
              </div>
            </footer>
          </>
        )}

        {/* ===== CATEGORY / SEARCH VIEW — show items ===== */}
        {(view === 'category' || searching) && (
          <>
            {/* Category header */}
            <div className="mb-8 md:mb-10">
              {searching ? (
                <div className="flex items-end justify-between flex-wrap gap-3">
                  <div>
                    <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-tan">{t({ tr: 'Arama', en: 'Search' })}</div>
                    <div className="font-display text-[30px] md:text-[40px] text-espresso italic">"{query}"</div>
                  </div>
                  <div className="font-mono text-[11px] tracking-[0.22em] uppercase text-espresso/55">{items.length} {t(UI.menu.items)}</div>
                </div>
              ) : currentCat && (
                <>
                  <div className="flex items-end justify-between flex-wrap gap-3">
                    <div>
                      <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-tan">
                        {t(UI.menuPage.section)} {String(idx + 1).padStart(2, '0')} / {String(cats.length).padStart(2, '0')}
                      </div>
                      <h2 className="font-display text-[36px] md:text-[54px] italic text-espresso leading-none mt-2">{t(currentCat.label)}</h2>
                    </div>
                    <div className="font-mono text-[11px] tracking-[0.22em] uppercase text-espresso/55">{items.length} {t(UI.menu.items)}</div>
                  </div>

                  {/* Category chips — quick jump, draggable/wheel-scroll on desktop */}
                  <CategoryChips
                    cats={cats}
                    catId={catId}
                    setCatId={setCatId}
                    countByCat={countByCat}
                    t={t}
                  />
                </>
              )}
            </div>

            {/* Items */}
            {items.length === 0 ? (
              <EmptyState t={t} onClear={() => { setQuery(''); setView('dashboard'); }}/>
            ) : (
              <div className="grid md:grid-cols-2 gap-x-10 gap-y-5">
                {items.map(it => (
                  <MenuLine key={it.id} item={it} t={t}
                    catLabel={searching ? t(cats.find(ca => ca.id === it.cat)?.label) : null}
                    onOpen={() => setDetail(it)}/>
                ))}
              </div>
            )}

            {/* Prev/Next navigation */}
            {!searching && view === 'category' && (
              <nav className="fmp-noprint mt-14 flex items-center justify-between gap-3 border-t border-espresso/15 pt-8">
                <button onClick={prev} disabled={idx <= 0}
                  className={`inline-flex items-center gap-3 rounded-2xl px-4 py-2.5 transition
                    ${idx <= 0 ? 'opacity-40 cursor-not-allowed' : 'hover:bg-white/60 border border-espresso/10'}`}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
                  <div className="text-left">
                    <div className="font-mono text-[9px] tracking-[0.25em] uppercase text-tan">{t({ tr: 'Önceki', en: 'Previous' })}</div>
                    <div className="font-display text-[14px] text-espresso italic leading-none mt-0.5">
                      {idx > 0 ? t(cats[idx - 1].label) : '—'}
                    </div>
                  </div>
                </button>

                <button onClick={() => setView('dashboard')}
                  className="hidden sm:inline-flex items-center gap-2 rounded-full bg-white/70 border border-espresso/15 px-4 py-2 text-[12px] text-espresso hover:bg-white transition">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="8" height="8" rx="1"/><rect x="13" y="3" width="8" height="8" rx="1"/><rect x="3" y="13" width="8" height="8" rx="1"/><rect x="13" y="13" width="8" height="8" rx="1"/></svg>
                  {t({ tr: 'Tüm Kategoriler', en: 'All Categories' })}
                </button>

                <button onClick={next} disabled={idx >= cats.length - 1}
                  className={`inline-flex items-center gap-3 rounded-2xl px-4 py-2.5 transition
                    ${idx >= cats.length - 1 ? 'opacity-40 cursor-not-allowed' : 'hover:bg-white/60 border border-espresso/10'}`}>
                  <div className="text-right">
                    <div className="font-mono text-[9px] tracking-[0.25em] uppercase text-tan">{t({ tr: 'Sonraki', en: 'Next' })}</div>
                    <div className="font-display text-[14px] text-espresso italic leading-none mt-0.5">
                      {idx < cats.length - 1 ? t(cats[idx + 1].label) : '—'}
                    </div>
                  </div>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                </button>
              </nav>
            )}
          </>
        )}

        <div className="mt-10 text-center font-mono text-[10px] tracking-[0.3em] uppercase text-espresso/45">
          — {t(b.name)} {t(b.tagline)} —
        </div>
      </article>

      {detail && <ItemDetail item={detail} onClose={() => setDetail(null)} c={c} t={t}/>}
    </div>
  );
}

/* ========= Category Card ========= */

function CategoryCard({ cat, count, onPick, t, i }) {
  const name = t(cat.label);
  return (
    <button onClick={onPick}
      className="cat-card-anim group relative rounded-2xl overflow-hidden aspect-[4/5] text-left hover:-translate-y-1 hover:shadow-[0_20px_50px_rgba(74,46,30,0.22)] transition-all duration-500"
      style={{ animationDelay: `${i * 40}ms`, background: TONE_BG[cat.tone] || TONE_BG.cream }}>
      {cat.image ? (
        <img src={cat.image} alt={name} className="absolute inset-0 w-full h-full object-cover"/>
      ) : (
        <div className="absolute inset-0">
          <CoffeeArt label={String(name).toUpperCase()} tone={cat.tone || 'cream'}/>
        </div>
      )}
      {/* Dark overlay at bottom for legibility */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent"/>

      {/* Count pill */}
      <div className="absolute top-3 right-3 bg-white/90 backdrop-blur rounded-full px-2.5 py-0.5 text-[10.5px] font-mono text-espresso shadow-sm">
        {count}
      </div>

      {/* Number */}
      <div className="absolute top-3 left-3 font-mono text-[10px] tracking-[0.25em] uppercase text-white/90">
        № {String(i + 1).padStart(2, '0')}
      </div>

      {/* Title + arrow */}
      <div className="absolute bottom-0 inset-x-0 p-3 md:p-4 flex items-end justify-between gap-2 text-white">
        <div>
          <div className="font-display text-[16px] md:text-[19px] leading-tight drop-shadow">{name}</div>
          <div className="text-[10px] font-mono tracking-widest uppercase opacity-80 mt-0.5">{count} {t(UI.menu.items)}</div>
        </div>
        <div className="h-8 w-8 rounded-full bg-white/95 grid place-items-center text-espresso group-hover:translate-x-0.5 transition shadow-lg">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
        </div>
      </div>
    </button>
  );
}

/* ========= Menu Line (items in a category) ========= */

function MenuLine({ item, t, catLabel, onOpen }) {
  return (
    <button onClick={onOpen}
      className="group flex items-start gap-4 p-2 rounded-xl hover:bg-white/50 transition-all text-left fmp-print-compact w-full">
      <div className="relative h-16 w-16 md:h-[72px] md:w-[72px] rounded-xl flex-none overflow-hidden border border-espresso/10"
        style={{ background: TONE_BG[item.tone] }}>
        {item.image ? (
          <img src={item.image} alt={t(item.name)} className="absolute inset-0 w-full h-full object-cover"/>
        ) : (
          <Placeholder tone={item.tone} label={String(t(item.name)).toUpperCase()} ratio="1/1" rounded="rounded-none" className="absolute inset-0"/>
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-baseline">
          <div className="flex items-baseline gap-2 min-w-0">
            <div className="font-display text-[18px] md:text-[20px] text-espresso leading-tight group-hover:text-cocoa transition">{t(item.name)}</div>
            {item.tag && <PrintBadge tag={item.tag}/>}
          </div>
          <span className="leader"/>
          <div className="font-display text-[18px] md:text-[20px] text-espresso tracking-tight flex-none">{item.price}</div>
        </div>
        {t(item.note) && (
          <div className="text-[12px] md:text-[12.5px] text-espresso/60 italic mt-1 pr-10 leading-relaxed line-clamp-2">
            {t(item.note)}
          </div>
        )}
        {catLabel && (
          <div className="mt-1 font-mono text-[9px] tracking-[0.22em] uppercase text-tan">{catLabel}</div>
        )}
      </div>
    </button>
  );
}

function EmptyState({ t, onClear }) {
  return (
    <div className="py-16 text-center">
      <div className="mx-auto h-16 w-16 rounded-full bg-latte/60 grid place-items-center text-[24px]">☕</div>
      <div className="font-display text-[22px] text-espresso mt-4">{t(UI.menu.noResult)}</div>
      <div className="text-[13px] text-espresso/65 mt-1">{t(UI.menu.tryAnother)}</div>
      <button onClick={onClear}
        className="mt-5 rounded-full bg-espresso text-cream px-5 py-2 text-[12px]">{t(UI.menu.clearFilters)}</button>
    </div>
  );
}

function PrintBadge({ tag }) {
  const map = {
    'Signature':  ['★', 'text-cocoa'],
    'Bestseller': ['♥', 'text-rose'],
    'New':        ['✦', 'text-tan'],
    'Summer':     ['☀', 'text-tan'],
  };
  const [sym, cls] = map[tag] || ['·', 'text-espresso/50'];
  return <span className={`text-[13px] ${cls} flex-none`} title={tag}>{sym}</span>;
}

/* ========= Item Detail Modal ========= */

function ItemDetail({ item, onClose, c, t }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const name = t(item.name);
  const note = t(item.note);
  const catLabel = t(c.menu.categories.find(x => x.id === item.cat)?.label);
  const showBranches = (c.visibility?.branches !== false) && Array.isArray(c.branches) && c.branches.length > 0;
  const branchNames = showBranches
    ? c.branches.slice(0, 3).map(br => String(t(br.name)).replace(/^Glamm\s+/i, '')).join(' · ')
    : '';

  return (
    <div className="fixed inset-0 z-[120] flex items-end md:items-center justify-center p-4 md:p-8 bg-ink/70 backdrop-blur-sm overflow-y-auto overscroll-contain"
      style={{ touchAction: 'pan-y' }}
      onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
        className="relative w-full max-w-3xl bg-cream rounded-[28px] overflow-hidden shadow-2xl grid md:grid-cols-2"
        style={{ animation: 'menuCardIn .35s cubic-bezier(.2,.7,.2,1)' }}>
        <div className="relative aspect-square md:aspect-auto md:min-h-[500px]" style={{ background: TONE_BG[item.tone] }}>
          {item.image ? (
            <img src={item.image} alt={name} className="absolute inset-0 w-full h-full object-cover"/>
          ) : (
            <Placeholder tone={item.tone} label={String(name).toUpperCase()} ratio="1/1" rounded="rounded-none" className="absolute inset-0"/>
          )}
          <button onClick={onClose}
            className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/90 backdrop-blur text-espresso grid place-items-center shadow-lg md:hidden">✕</button>
        </div>
        <div className="flex flex-col">
          <button onClick={onClose}
            className="hidden md:grid absolute top-5 right-5 h-10 w-10 rounded-full bg-white/80 text-espresso border border-espresso/15 place-items-center hover:bg-white">✕</button>
          <div className="px-7 md:px-10 pt-8 md:pt-12 pb-6 flex-1 flex flex-col items-center text-center">
            <div className="font-mono text-[11px] tracking-[0.25em] uppercase text-tan">{catLabel}</div>
            <h3 className="font-display text-[32px] md:text-[42px] leading-[1.04] text-espresso mt-2">{name}</h3>
            {note && (
              <div className="mt-6 w-full max-w-[420px]">
                <div className="font-mono text-[10.5px] tracking-[0.22em] uppercase text-tan mb-2.5">
                  {t({ tr: 'Açıklama', en: 'Description' })}
                </div>
                <div className="rounded-2xl bg-white/70 border border-espresso/10 px-4 py-3 text-[13.5px] text-espresso/80 leading-relaxed">
                  {note}
                </div>
              </div>
            )}
          </div>
          <div className="px-7 md:px-10 py-5 border-t border-espresso/10 bg-white/40 flex flex-col items-center text-center gap-2">
            <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-tan">{t(UI.menu.price)}</div>
            <div className="font-display text-[28px] text-espresso leading-none">{item.price}</div>
            {showBranches && branchNames && (
              <>
                <div className="mt-3 font-mono text-[10px] tracking-[0.2em] uppercase text-tan">{t({ tr: 'Şubelerimizde', en: 'At our stores' })}</div>
                <div className="text-[12px] text-espresso/75">{branchNames}</div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function CategoryChips({ cats, catId, setCatId, countByCat, t }) {
  const ref = useRef(null);
  const drag = useRef({ active: false, startX: 0, startScroll: 0, moved: false });
  const [edges, setEdges] = useState({ left: false, right: false });

  const updateEdges = () => {
    const el = ref.current;
    if (!el) return;
    setEdges({
      left: el.scrollLeft > 4,
      right: el.scrollLeft + el.clientWidth < el.scrollWidth - 4,
    });
  };

  useEffect(() => {
    updateEdges();
    const el = ref.current;
    if (!el) return;
    const onScroll = () => updateEdges();
    el.addEventListener('scroll', onScroll, { passive: true });
    const onResize = () => updateEdges();
    window.addEventListener('resize', onResize);
    return () => { el.removeEventListener('scroll', onScroll); window.removeEventListener('resize', onResize); };
  }, [cats.length]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const active = el.querySelector('[data-active="true"]');
    if (active) active.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  }, [catId]);

  const onWheel = (e) => {
    const el = ref.current;
    if (!el) return;
    if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
      el.scrollLeft += e.deltaY;
      e.preventDefault();
    }
  };
  const onMouseDown = (e) => {
    const el = ref.current;
    if (!el) return;
    if (e.button !== 0) return;
    drag.current = { active: true, startX: e.clientX, startScroll: el.scrollLeft, moved: false };
  };
  const onMouseMove = (e) => {
    if (!drag.current.active) return;
    const el = ref.current;
    const dx = e.clientX - drag.current.startX;
    if (Math.abs(dx) > 4) drag.current.moved = true;
    if (drag.current.moved) {
      el.scrollLeft = drag.current.startScroll - dx;
      e.preventDefault();
    }
  };
  const endDrag = () => { setTimeout(() => { drag.current.active = false; }, 0); };
  const handleChipClick = (id) => (e) => {
    if (drag.current.moved) { e.preventDefault(); e.stopPropagation(); drag.current.moved = false; return; }
    setCatId(id);
  };

  const nudge = (dir) => {
    const el = ref.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.max(180, el.clientWidth * 0.7), behavior: 'smooth' });
  };

  return (
    <div className="mt-6 relative">
      {edges.left && (
        <button aria-label="scroll left" onClick={() => nudge(-1)}
          className="hidden md:flex items-center justify-center absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-white shadow-md border border-espresso/10 text-espresso hover:bg-cream transition">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 18l-6-6 6-6"/></svg>
        </button>
      )}
      {edges.right && (
        <button aria-label="scroll right" onClick={() => nudge(1)}
          className="hidden md:flex items-center justify-center absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-white shadow-md border border-espresso/10 text-espresso hover:bg-cream transition">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 6l6 6-6 6"/></svg>
        </button>
      )}
      <div
        ref={ref}
        onWheel={onWheel}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={endDrag}
        onMouseLeave={endDrag}
        className="flex items-center gap-2 overflow-x-auto no-scrollbar cursor-grab active:cursor-grabbing px-1 md:px-10"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        {cats.map(x => {
          const active = catId === x.id;
          return (
            <button
              key={x.id}
              data-active={active}
              onClick={handleChipClick(x.id)}
              className={`flex-none rounded-full px-4 py-2 text-[13px] font-medium tracking-wide border transition shadow-sm
                ${active
                  ? 'bg-espresso text-cream border-espresso shadow-[0_6px_16px_rgba(74,46,30,0.25)] scale-[1.03]'
                  : 'bg-white border-espresso/20 text-espresso/85 hover:bg-cream hover:border-espresso/40'}`}
            >
              {t(x.label)}
              <span className={`font-mono text-[10px] ml-1.5 ${active ? 'text-cream/75' : 'text-espresso/50'}`}>
                {countByCat[x.id] || 0}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
